/**
 * Delivery Time Calculation Utilities
 *
 * Calculates estimated delivery times based on provider settings
 * and current time.
 */

import { addHours, setHours, setMinutes, setSeconds, setMilliseconds, isAfter, addDays } from 'date-fns';
import type { DeliveryTimeSettings } from '../../services/provider.service';

/**
 * Calculate estimated delivery time based on provider settings
 *
 * @param settings - Provider's delivery time settings
 * @param orderTime - Time when order is placed (defaults to now)
 * @returns Estimated delivery date/time
 */
export function calculateDeliveryEstimate(
  settings: DeliveryTimeSettings | undefined,
  orderTime: Date = new Date()
): Date {
  // If delivery time settings not enabled or not configured, default to next day noon
  if (!settings || !settings.enabled) {
    const tomorrow = addDays(orderTime, 1);
    return setHours(setMinutes(setSeconds(setMilliseconds(tomorrow, 0), 0), 0), 12);
  }

  switch (settings.type) {
    case 'same_day': {
      // Same day delivery if ordered before cutoff time
      const cutoffHour = settings.sameDayCutoffHour ?? 14; // Default 2pm
      const deliveryHour = settings.sameDayDeliveryHour ?? 17; // Default 5pm

      const cutoffTime = setHours(
        setMinutes(setSeconds(setMilliseconds(orderTime, 0), 0), 0),
        cutoffHour
      );

      if (isAfter(orderTime, cutoffTime)) {
        // After cutoff, deliver next day
        const tomorrow = addDays(orderTime, 1);
        const nextDayHour = settings.nextDayDeliveryHour ?? 12;
        return setHours(setMinutes(setSeconds(setMilliseconds(tomorrow, 0), 0), 0), nextDayHour);
      } else {
        // Before cutoff, deliver today
        return setHours(
          setMinutes(setSeconds(setMilliseconds(orderTime, 0), 0), 0),
          deliveryHour
        );
      }
    }

    case 'next_day': {
      // Always next day delivery
      const tomorrow = addDays(orderTime, 1);
      const deliveryHour = settings.nextDayDeliveryHour ?? 12;
      return setHours(setMinutes(setSeconds(setMilliseconds(tomorrow, 0), 0), 0), deliveryHour);
    }

    case 'custom': {
      // Custom hours from order time
      const hours = settings.customEstimateHours ?? 24;
      return addHours(orderTime, hours);
    }

    default: {
      // Fallback to next day noon
      const tomorrow = addDays(orderTime, 1);
      return setHours(setMinutes(setSeconds(setMilliseconds(tomorrow, 0), 0), 0), 12);
    }
  }
}

/**
 * Get default delivery time settings for testing/fallback
 */
export function getDefaultDeliverySettings(): DeliveryTimeSettings {
  return {
    type: 'same_day',
    sameDayCutoffHour: 14, // 2pm
    sameDayDeliveryHour: 17, // 5pm
    nextDayDeliveryHour: 12, // noon
    enabled: true,
  };
}
